/**
 * Hook optimisé pour les actions sur les contacts
 * N'utilise PAS useCRUD, donc ne charge pas toutes les données
 * Utilisé dans ContactDetailPage pour éviter les requêtes inutiles
 */

import { useCallback } from 'react';
import { useSupabase } from './useSupabase';
import { useAuth } from './useAuth';
import type { Contact } from '../types/contact';
import type { ContactDeletionImpact, ContactDeletionResult } from '../types/contactDeletion';
import { monitorQuery } from '../lib/supabase-monitor';
import { useUsers } from '../contexts/UsersContext';
import { logEntityUpdate } from '../lib/timeline-logger';

export function useContactActions() {
  const supabase = useSupabase();
  const { user } = useAuth(); // ✅ Pour le logging
  const { usersMap } = useUsers(); // ✅ Cache des utilisateurs

  /**
   * Récupérer un contact par ID avec toutes ses relations
   */
  const getContactById = useCallback(
    async (id: string): Promise<Contact | null> => {
      try {
        const { data, error } = await monitorQuery(
          'contacts',
          'select',
          `id=${id}`,
          (() =>
            supabase
              .from('contacts')
              .select(
                `
            *,
            origin:origins(id, name, label),
            products:contact_products(
              product:products(id, name)
            )
          `
              )
              .eq('id', id)
              .single()) as any,
          'useContactActions.getContactById'
        );

        if (error) throw error;
        if (!data) return null;

        // Enrichir avec l'utilisateur assigné depuis le cache
        const contact = data as any;

        // ✅ OPTIMISATION : Utiliser le cache au lieu d'appeler RPC
        if (contact.assigned_to && usersMap[contact.assigned_to]) {
          contact.assigned_user = usersMap[contact.assigned_to];
        }

        // Aplatir la structure des produits
        return {
          ...contact,
          products: contact.products?.map((cp: any) => cp.product).filter(Boolean) || [],
          assigned_user: contact.assigned_user || null,
        };
      } catch (err) {
        console.error('[useContactActions] getContactById error:', err);
        return null;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [supabase]
  );

  /**
   * Mettre à jour un contact
   */
  const updateContact = useCallback(
    async (id: string, data: Partial<Contact>): Promise<boolean> => {
      try {
        // 1. ✅ Récupérer les données AVANT la modification pour le logging
        const oldContact = await getContactById(id);
        if (!oldContact) {
          console.error('[useContactActions] updateContact: contact not found');
          return false;
        }

        // 2. ✅ CORRECTIF : Extraire product_ids (champ virtuel, pas une vraie colonne)
        const { product_ids, ...contactData } = data as any;

        // 3. Nettoyer les chaînes vides pour les UUID optionnels
        const cleaned: any = { ...contactData };
        if (cleaned.origin_id === '') cleaned.origin_id = null;
        if (cleaned.assigned_to === '') cleaned.assigned_to = null;

        // 4. Mettre à jour les informations du contact (si non vide)
        if (Object.keys(cleaned).length > 0) {
          const { error } = await monitorQuery(
            'contacts',
            'update',
            `id=${id}`,
            (() => supabase.from('contacts').update(cleaned).eq('id', id)) as any,
            'useContactActions.updateContact'
          );

          if (error) return false;
        }

        // 5. Gérer les produits associés si fournis
        if (product_ids !== undefined) {
          // Supprimer toutes les associations existantes
          const { error: deleteError } = await supabase
            .from('contact_products')
            .delete()
            .eq('contact_id', id);

          if (deleteError) throw deleteError;

          // Recréer les associations
          if (product_ids.length > 0) {
            const contactProducts = product_ids.map((product_id: string) => ({
              contact_id: id,
              product_id,
            }));

            const { error: linkError } = await supabase
              .from('contact_products')
              .insert(contactProducts);

            if (linkError) throw linkError;
          }
        }

        // 6. ✅ LOGGER LES CHANGEMENTS (similaire à useCRUD)
        const newData = { ...oldContact, ...cleaned };

        // Récupérer la liste des champs qui ont changé (sauf assigned_to qui est loggé séparément)
        const changedFields = Object.keys(cleaned).filter((key) => key !== 'assigned_to');

        if (changedFields.length > 0) {
          await logEntityUpdate(
            supabase,
            'contact',
            id,
            oldContact,
            newData,
            user?.id || null,
            changedFields
          );
        }

        return true;
      } catch (err) {
        console.error('[useContactActions] updateContact error:', err);
        return false;
      }
    },

    [supabase, getContactById, user]
  );

  /**
   * Dry-run de suppression : retourne l'impact (dossiers à supprimer /
   * détacher, équipages actifs, gardes superadmin/force) sans rien modifier.
   * CRM-15 — RPC delete_contact_safely (00159).
   */
  const previewContactDeletion = useCallback(
    async (id: string): Promise<ContactDeletionResult> => {
      const { data, error } = await supabase.rpc('delete_contact_safely', {
        p_contact_id: id,
        p_dry_run: true,
      });
      if (error) {
        console.error('[useContactActions] previewContactDeletion RPC error:', error);
        return { success: false, code: error.message };
      }
      return { success: true, impact: data as ContactDeletionImpact };
    },
    [supabase]
  );

  /**
   * Supprimer un contact via la RPC transactionnelle (remplace le DELETE
   * direct qui échouait en 23503 silencieux dès qu'un payment_case pointait
   * la fiche). Le code d'erreur métier est remonté pour mapping FR
   * (contactDeleteErrors).
   */
  const deleteContactSafely = useCallback(
    async (
      id: string,
      force: boolean
    ): Promise<{ success: true } | { success: false; code: string }> => {
      const { error } = await supabase.rpc('delete_contact_safely', {
        p_contact_id: id,
        p_force: force,
      });
      if (error) {
        console.error('[useContactActions] deleteContactSafely RPC error:', error);
        return { success: false, code: error.message };
      }
      return { success: true };
    },
    [supabase]
  );

  /**
   * Retirer l'assignation d'un utilisateur
   */
  const unassignUser = useCallback(
    async (contactId: string): Promise<boolean> => {
      try {
        const { error } = await monitorQuery(
          'contacts',
          'update',
          `id=${contactId} (unassign)`,
          (() =>
            supabase.from('contacts').update({ assigned_to: null }).eq('id', contactId)) as any,
          'useContactActions.unassignUser'
        );

        return !error;
      } catch (err) {
        console.error('[useContactActions] unassignUser error:', err);
        return false;
      }
    },

    [supabase]
  );

  return {
    getContactById,
    updateContact,
    previewContactDeletion,
    deleteContactSafely,
    unassignUser,
  };
}
